import type { FilterOptionValue } from 'features/filters/types'

export type FilterValueSortBy = 'count' | 'title'
export type FilterValueSortDirection = 'ASC' | 'DESC'

export interface FilterValueSortOptions {
  sortBy?: FilterValueSortBy
  sortDirection?: FilterValueSortDirection
}

/**
 * Sort filter option rows so the most relevant values float to the top.
 *
 * Default behavior: all `n_posts` defined → descending by count,
 * alphabetical tie-break. Any `n_posts === undefined` → alphabetical
 * only (avoids reshuffle when the remaining counts land). `n_posts === 0`
 * is a real count of zero (matches `FilterOptionPostCountCell`'s
 * `undefined` → skeleton, 0 → "0" text contract). Returns a new array;
 * never mutates input.
 *
 * When `isPreferred` is supplied (e.g. `isValueSelected`), preferred
 * rows float to the top of the list and each bucket is sorted
 * independently. Used by FilterPopover for the selected-first ordering.
 *
 * When `options.sortBy` / `options.sortDirection` are supplied, they
 * override the default count-desc-with-alpha-tiebreak. `sortBy='count'`
 * sorts by `n_posts` (with alpha tiebreak), `sortBy='title'` sorts
 * alphabetically by `value`. Sites with unknown counts always fall back
 * to alphabetical sort regardless of `sortBy`.
 */
export const sortFilterOptionValues = (
  values: readonly FilterOptionValue[],
  isPreferred?: (value: FilterOptionValue) => boolean,
  options?: FilterValueSortOptions,
): FilterOptionValue[] => {
  if (isPreferred) {
    const preferred: FilterOptionValue[] = []
    const rest: FilterOptionValue[] = []
    for (const v of values) (isPreferred(v) ? preferred : rest).push(v)
    return [
      ...sortFilterOptionValues(preferred, undefined, options),
      ...sortFilterOptionValues(rest, undefined, options),
    ]
  }
  const sortBy = options?.sortBy ?? 'count'
  const sortDirection =
    options?.sortDirection ?? (sortBy === 'count' ? 'DESC' : 'ASC')
  const allCountsKnown = values.every((v) => v.n_posts !== undefined)
  const copy = [...values]
  if (sortBy === 'count' && allCountsKnown) {
    return copy.sort((a, b) => {
      const aCount = a.n_posts ?? 0
      const bCount = b.n_posts ?? 0
      const diff = sortDirection === 'DESC' ? bCount - aCount : aCount - bCount
      if (diff !== 0) return diff
      return (a.value || '').localeCompare(b.value || '')
    })
  }
  return copy.sort((a, b) => {
    const cmp = (a.value || '').localeCompare(b.value || '')
    return sortBy === 'title' && sortDirection === 'DESC' ? -cmp : cmp
  })
}
